import { useState } from "react";
import { ArrowRight, Play, Quote } from "lucide-react";
import { Nebula } from "@/components/Nebula";
import { EngineGrid } from "@/components/EngineGrid";
import { CaseMarquee } from "@/components/CaseMarquee";
import { LeadFormDialog } from "@/components/LeadFormDialog";
import { useT } from "@/lib/i18n";
import { config } from "@/lib/config";
import { MEGA_MENU_EXTERNAL_URLS } from "@/i18n/menu-external-urls";
import proyaLogo from "@/assets/home/CaseMarquee/PROYA-logo.png";
import proyaThumb from "@/assets/home/CaseMarquee/PROYA-thumb.png";
import dongchediLogo from "@/assets/home/CaseMarquee/dongchedi-logo.png";
import dongchediThumb from "@/assets/home/CaseMarquee/dongchedi-thumb.png";
import bluettiLogo from "@/assets/home/CaseMarquee/BLUETTI-logo.png";
import bluettiThumb from "@/assets/home/CaseMarquee/BLUETTI-thumb.png";
import zdeerLogo from "@/assets/home/CaseMarquee/zdeer-logo.png";
import zdeerThumb from "@/assets/home/CaseMarquee/zdeer-htumb.png";
import imoreLogo from "@/assets/home/CaseMarquee/1MORE-logo.png";
import imoreThumb from "@/assets/home/CaseMarquee/1MORE-thumb.png";
import trajectryLogo from "@/assets/home/CaseMarquee/TRAJECTRY-logo.png";
import trajectryThumb from "@/assets/home/CaseMarquee/TRAJECTRY-thumb.png";
import timekettleLogo from "@/assets/home/CaseMarquee/Timekettle-logo.png";
import timekettleThumb from "@/assets/home/CaseMarquee/Timekettle-thumb.jpg";
import xingyunLogo from "@/assets/home/CaseMarquee/XINGYUN-logo.png";
import xingyunThumb from "@/assets/home/CaseMarquee/XINGYUN-thumb.png";
import matrixLogo from "@/assets/home/CaseMarquee/matrix-logo.png";
import matrixThumb from "@/assets/home/CaseMarquee/matrix-thumb.jpg";
import logo01 from "@/assets/home/logo/01_PROYA.png";
import logo02 from "@/assets/home/logo/02_P&G_.png";
import logo03 from "@/assets/home/logo/03_PLUG_AND_PLAY_CHINA.png";
import logo04 from "@/assets/home/logo/04_Transsion.png";
import logo05 from "@/assets/home/logo/05_KERING_FOUNDATION.png";
import logo06 from "@/assets/home/logo/06_dingchedi.png";
import logo07 from "@/assets/home/logo/07_CARSLAN.png";
import logo08 from "@/assets/home/logo/08_lebo.png";
import logo09 from "@/assets/home/logo/09_Timekettle.png";
import logo10 from "@/assets/home/logo/10_XINGYUN_GROUP.png";
import logo11 from "@/assets/home/logo/11_BLUETTI.png";
import logo12 from "@/assets/home/logo/12_1MORE.png";
import logo13 from "@/assets/home/logo/13_LEE_KUM_KEE.png";
import logo14 from "@/assets/home/logo/14_FUNNY_FUZZY.png";
import logo15 from "@/assets/home/logo/15_FILIPPO_LORETI.png";
import logo16 from "@/assets/home/logo/16_zdeer.png";
import logo17 from "@/assets/home/logo/17_TRAJECTRY.png";
import logo18 from "@/assets/home/logo/18_IRS_Group.png";
import logo19 from "@/assets/home/logo/19_reenda.png";
import logo20 from "@/assets/home/logo/20_C77GLOBAL.png";
import logo21 from "@/assets/home/logo/21_WaytoAGI.png";
import logo22 from "@/assets/home/logo/22_HONG.png";
import logo23 from "@/assets/home/logo/23_ESTEE_LAUDER.png";
import logo24 from "@/assets/home/logo/24_matrix.png";
import logo25 from "@/assets/home/logo/25_CHANGAN.png";

const clientLogos = [
  logo01, logo02, logo03, logo04, logo05, logo06, logo07, logo08, logo09, logo10,
  logo11, logo12, logo13, logo14, logo15, logo16, logo17, logo18, logo19, logo20,
  logo21, logo22, logo23, logo24, logo25,
];

interface HomePageProps {
  activeTab?: string;
  setActiveTab?: (tab: string) => void;
  onScrollToFooter?: () => void;
  onHideFooter?: () => void;
}

function SectionHeader({ kicker, title, sub }: { kicker?: string; title: string; sub?: string }) {
  return (
    <div className="text-center max-w-3xl mx-auto mb-16">
      {kicker && <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">{kicker}</p>}
      <h2 className="text-4xl md:text-5xl font-semibold tracking-tight">{title}</h2>
      {sub && <p className="mt-6 text-base text-muted-foreground/90 leading-relaxed">{sub}</p>}
    </div>
  );
}

const HomePage: React.FC<HomePageProps> = () => {
  const t = useT();
  const [formOpen, setFormOpen] = useState(false);

  const engines = [
    {
      name: "OranHub",
      tag: t("Insight & Strategy", "洞察决策"),
      line: t("Read the market. Build a plan that works.", "读懂市场，拿到一份真正能落地的计划。"),
      href: MEGA_MENU_EXTERNAL_URLS["menu.prod.insights"],
    },
    {
      name: "OranSim",
      tag: t("Simulation & Foresight", "预测引擎"),
      line: t(
        "Test campaigns in a parallel universe — before you spend.",
        "在花钱之前，先在平行世界里把整场营销跑一遍。",
      ),
      href: MEGA_MENU_EXTERNAL_URLS["menu.prod.strategy"],
    },
    {
      name: "OranGen",
      tag: t("Content Factory", "内容工厂"),
      line: t(
        "Turn one winning idea into 30+ ready-to-publish creatives.",
        "一个最优创意，裂变成 30+ 条可直接发布的内容。",
      ),
      href: MEGA_MENU_EXTERNAL_URLS["menu.prod.contentFactory"],
    },
    {
      name: "OranMed",
      tag: t("Distributed Media", "分布式媒介"),
      line: t(
        "Distribute at scale — through owned accounts and real creators.",
        "通过自有账号和真实创作者，实现规模化分发。",
      ),
      // 待用户提供 OranMed 外链 — 暂留占位
    },
  ];

  const testimonials = [
    {
      tag: t("Content Production", "内容生产"),
      metric: t("Systematic & Reusable", "系统且可复用"),
      quote: t(
        "Oran AI helped our performance video team integrate AI into daily content production, making script ideation, viral-content analysis, video replication, and asset accumulation more systematic, stable, and reusable.",
        "橙果视界帮助我们的信息流视频团队把 AI 融入日常内容生产，让脚本创意、爆款拆解、视频复刻和素材沉淀变得更加系统、稳定、可复用。",
      ),
      name: t("PROYA", "珀莱雅"),
      role: t("Brand", "品牌"),
      meta: t("Beauty · China", "美妆 · 中国"),
    },
    {
      tag: t("Product Launch", "新品上线"),
      metric: t("Faster Visual Output", "更快产出视觉素材"),
      quote: t(
        "In the past, the most time-consuming part of a new product launch was producing complete sets of product images and scenario-based visuals. After working with Oran AI, we were able to quickly generate consistent product visuals for different platforms and audience segments, making updates to our official website, e-commerce detail pages, and marketing materials noticeably smoother.",
        "过去新品上线最耗时间的是成套产品图和场景素材。和橙果视界合作后，我们可以围绕不同平台、不同人群快速生成统一风格的产品视觉，官网、电商详情页和营销素材的更新节奏明显更顺了。",
      ),
      name: t("Zdeer", "左点"),
      role: t("Brand", "品牌"),
      meta: t("Healthcare · China", "医疗 · 中国"),
    },
    {
      tag: t("Digital Assets", "数字资产"),
      metric: t("Systematic Management", "系统化管理"),
      quote: t(
        "In the AIGC era, the value of brand assets lies not only in content output, but also in whether those assets can be managed, reused, and circulated securely. Oran AI helped us build the digital asset foundation we needed, moving our content operations from fragmented storage toward systematic management.",
        "AIGC 时代，品牌资产的价值不只在于产出，更在于能否被管理、复用和安全流转。橙果视界帮我们补上了数字资产底座，让内容运营从'分散存放'走向'系统管理'。",
      ),
      name: t("Matrix Design", "矩阵纵横"),
      role: t("Design Firm", "设计公司"),
      meta: t("Interior Design · China", "室内设计 · 中国"),
    },
    {
      tag: t("Social Media", "社媒内容"),
      metric: t("Natural & Engaging", "自然且易互动"),
      quote: t(
        "For headphone content, the biggest challenge is avoiding visuals that look too much like parameter demonstrations, which makes it hard for users to perceive real wearing and usage scenarios. After working with Oran AI, we were able to generate visual assets for different social media platforms more efficiently, expressing sound quality, wearing comfort, and lifestyle atmosphere in a more natural way—closer to the kind of content users are willing to engage with and be influenced by.",
        "耳机内容最怕拍得太像参数展示，用户很难感受到真实佩戴和使用场景。和橙果视界合作后，我们能更快生成适合不同社媒平台的视觉素材，把音质、佩戴感和生活方式氛围表达得更自然，也更接近用户愿意互动和种草的内容形态。",
      ),
      name: t("1MORE", "万魔"),
      role: t("Brand", "品牌"),
      meta: t("Audio · Global", "音频 · 全球"),
    },
    {
      tag: t("Global Communication", "全球传播"),
      metric: t("Unified Expression", "统一表达"),
      quote: t(
        "For Timekettle, the key to overseas communication is not only showcasing the product, but also helping users in different markets quickly understand the value of instant communication. Oran AI helped us establish a more unified visual and short-video content expression, allowing the product to remain consistent, professional, and easy to understand across social media advertising, content updates, and multi-channel communication.",
        "对 Timekettle 来说，海外传播的关键不只是展示产品，而是让不同市场的用户快速理解即时沟通的价值。橙果视界帮助我们建立了更统一的视觉与短视频内容表达，让产品在社媒投放、内容更新和多渠道传播中保持一致、专业且易于理解。",
      ),
      name: t("Timekettle", "时空壶"),
      role: t("Brand", "品牌"),
      meta: t("Translation · Global", "翻译 · 全球"),
    },
  ];

  return (
    <div className="landing-v2 min-h-screen text-foreground">
      {/* HERO */}
      <section className="relative min-h-screen overflow-hidden pt-28">
        <Nebula groups={7} perGroup={70}>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-background/80 pointer-events-none" />
          <div className="relative h-full min-h-screen flex items-center justify-center">
            <div className="relative z-10 text-center px-6 max-w-[min(1400px,90vw)] mx-auto">
              <a href="https://whitebook.photog.art/background" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 glass px-4 py-1.5 text-xs text-muted-foreground mb-8 hover:text-foreground transition-colors">
                <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--brand))] animate-pulse-glow" />
                {t("OranAI Whitepaper Is Now Live!", "OranAI 白皮书已上线！")}
              </a>
              <h1 className="text-5xl md:text-7xl font-semibold tracking-tight leading-[1.05] text-foreground">
                {t(<>Your AI MCN</>, <>你的 AI 全域达人网络</>)}
              </h1>
              <p className="mt-10 text-xl md:text-2xl text-foreground font-medium">
                {t("Empower Every Brand to Be Heard", "让世界听见每一个品牌的声音")}
              </p>
              <p className="mt-6 max-w-2xl mx-auto text-base md:text-lg text-muted-foreground leading-relaxed">
                {t(
                  "Creator ecosystem defines influence, all-domain intelligence ensures certainty — from distribution to amplification, prediction to efficient reach, delivering full-funnel brand growth coverage.",
                  "达人生态定义影响力，全域智能锁定确定性。从内容分发到势能放大，从效果预测到高效触达，让品牌增长再无盲区",
                )}
              </p>
              <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                <button className="btn-primary" onClick={() => setFormOpen(true)}>
                  {t("Start Free", "免费开始")} <ArrowRight size={16} />
                </button>
                <a className="btn-secondary" href="/company">
                  <Play size={14} /> {t("Watch the Demo", "观看 Demo")}
                </a>
              </div>
              <div className="mt-16 flex flex-wrap justify-center gap-x-10 gap-y-3 text-xs text-muted-foreground">
                <span>
                  <span className="text-foreground font-medium">200+</span> {t("Brands & Creators", "品牌与创作者")}
                </span>
                <span>
                  <span className="text-foreground font-medium">1.2M</span> {t("Pieces Distributed", "内容分发")}
                </span>
                <span>
                  <span className="text-foreground font-medium">5</span> {t("Platforms Covered", "覆盖平台")}
                </span>
              </div>
            </div>
          </div>
        </Nebula>
      </section>

      {/* CLIENT LOGO MARQUEE */}
      <section className="relative mt-0 pb-20 overflow-hidden">
        <div className="relative mx-auto max-w-[min(1760px,95vw)] px-6 h-24 overflow-hidden">
          <div className="pointer-events-none absolute inset-y-0 left-0 w-32 z-10 bg-gradient-to-r from-background to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-32 z-10 bg-gradient-to-l from-background to-transparent" />
          <div className="flex gap-8 animate-marquee-x w-max">
            {[...clientLogos, ...clientLogos, ...clientLogos].map((logo, i) => (
              <img
                key={i}
                src={logo}
                alt={`Client logo ${i + 1}`}
                className="h-12 w-auto object-contain opacity-80 hover:opacity-100 transition-opacity"
              />
            ))}
          </div>
        </div>
      </section>

      {/* FOUR ENGINES */}
      <section className="relative py-32 px-6">
        <div className="mx-auto max-w-[min(1760px,95vw)]">
          <SectionHeader
            kicker={t("Product Matrix", "产品矩阵")}
            title={t("Four Engines, One Closed Loop.", "四大引擎，一个闭环。")}
            sub={t(
              "Insight fuels simulation. Simulation directs creation. Creation drives distribution. Every signal returns as the starting point of your next growth.",
              "洞察驱动预演，预演指导生产，生产带动分发，每一个信号回流成下一次增长的起点。",
            )}
          />
          <EngineGrid engines={engines} t={t} />
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="relative py-32 px-6 overflow-hidden">
        <div className="mx-auto max-w-[min(1760px,95vw)]">
          <SectionHeader
            kicker={t("Voices", "声音")}
            title={t("What Our Partners Say.", "客户与创作者的声音。")}
            sub={t(
              "Real teams, real numbers — from first idea to first revenue.",
              "真实团队，真实数据 —— 从第一个想法到第一笔收入。",
            )}
          />
          <div className="relative w-full overflow-hidden rounded-2xl">
            <div className="pointer-events-none absolute inset-y-0 left-0 w-[18%] z-10 bg-gradient-to-r from-background to-transparent" />
            <div className="pointer-events-none absolute inset-y-0 right-0 w-[18%] z-10 bg-gradient-to-l from-background to-transparent" />
            <div className="flex gap-8 animate-marquee-x w-max py-4">
              {[...testimonials, ...testimonials, ...testimonials].map((tm, i) => {
                const initial = (tm.name || tm.role).trim().charAt(0).toUpperCase();
                return (
                  <article
                    key={i}
                    className="testi-card glass nebula-border relative p-10 flex flex-col shrink-0"
                    style={{ width: "min(42vw, 600px)", minHeight: "360px" }}
                  >
                    <Quote
                      aria-hidden
                      size={120}
                      className="absolute -top-4 -right-2 opacity-[0.05] pointer-events-none"
                      style={{ color: "hsl(var(--brand))" }}
                    />
                    <div className="flex items-center justify-between gap-4 mb-6 relative">
                      <span className="text-xs px-3 py-1 glass rounded-full tracking-wider">{tm.tag}</span>
                      <span className="text-xs uppercase tracking-[0.2em] brand-text font-medium">{tm.metric}</span>
                    </div>
                    <p className="text-lg md:text-xl font-medium leading-relaxed text-foreground/95 flex-1">
                      &ldquo;{tm.quote}&rdquo;
                    </p>
                    <div className="divider-nebula my-6" />
                    <div className="flex items-center gap-4">
                      <div
                        className="h-11 w-11 rounded-full grid place-items-center text-sm font-semibold"
                        style={{
                          background:
                            "linear-gradient(135deg, color-mix(in oklab, hsl(var(--brand)) 35%, transparent), oklch(1 0 0 / 0.06))",
                          border: "1px solid color-mix(in oklab, hsl(var(--brand)) 45%, transparent)",
                          color: "hsl(var(--foreground))",
                        }}
                      >
                        {initial}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">
                          {tm.name}
                          <span className="text-muted-foreground"> · {tm.role}</span>
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">{tm.meta}</p>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CASE STUDIES */}
      <section className="relative py-32 px-6">
        <div className="mx-auto max-w-[min(1760px,95vw)]">
          <SectionHeader
            title={t("From Insight to Impact — In Days, Not Months.", "洞察到触达，只需几天。")}
            sub={t(
              "See how real teams use ORANAI to research, predict, create, and reach.",
              "看看真实团队如何用 ORANAI 完成调研、预演、创造和触达。",
            )}
          />
          <CaseMarquee
            readLabel={t("Read Full Case", "阅读完整案例")}
            cases={[
              {
                brand: t("PROYA", "珀莱雅"),
                logo: proyaLogo,
                thumb: proyaThumb,
                tags: [t("Beauty", "美妆"), t("Content Production", "内容生产")],
                title: t("PROYA", "珀莱雅"),
                desc: t(
                  "Oran AI provided AI-powered technical enablement for the client’s Douyin information-flow video production team, covering core roles including directors, editors, remix editors, and shooting teams. Centered on real content production scenarios, the project helped the team build a shared understanding of AI, strengthen structured prompting capabilities, and apply AI across key stages including script generation, viral video analysis, first-three-second hook analysis, video replication, AI opening-video creation, asset organization, and workflow automation. Through systematic enablement, the team significantly improved execution efficiency across creative output, video replication, asset management, and content testing, establishing an initial AI-powered production workflow from analysis and generation to replication, asset accumulation, and reuse.",
                  "橙果视界为客户抖音信息流视频制作团队提供 AI 信息流视频技术陪跑服务，覆盖编导、剪辑、混剪、摄影等核心岗位。项目围绕真实信息流内容生产场景展开，通过 AI 认知统一、结构化提示词训练与实战任务演练，帮助团队将 AI 应用于脚本生成、爆款视频拆解、前三秒 Hook 分析、视频复刻、AI 开头生成、素材整理及工作流自动化等关键环节。经过系统陪跑，团队在创意产出、视频复刻、素材整理与内容测试中的执行效率显著提升，初步形成从“拆解—生成—复刻—沉淀—复用”的 AI 内容生产链路。",
                ),
                bullets: [
                  t("AI Creative Production Efficiency 60%+", "AI 创意生产效率提升 60%+"),
                  t("Information-Flow Content Testing Efficiency 40%+", "信息流内容测试链路提效 40%+"),
                  t("Core Role AI Capability Coverage 50+ Team Members", "核心岗位 AI 能力覆盖 50+ 人"),
                ],
              },
              {
                brand: t("dongchedi", "懂车帝"),
                logo: dongchediLogo,
                thumb: dongchediThumb,
                tags: [t("Automotive", "汽车"), t("Content Generation", "内容生成")],
                title: t("懂车帝", "懂车帝"),
                desc: t(
                  "Oran AI designed an AI-powered batch content generation solution for new vehicle testing scenarios, integrating single-car image posts, price-inquiry templates, video storyboard assets, and AI product-description structures into a configurable, reusable, and scalable production workflow. By combining vehicle asset uploads, template selection, text replacement, code-based batch layout generation, and AI content generation, the solution helped transform manual automotive content production into a high-output, cost-efficient, and rapidly testable intelligent production system, significantly improving daily content output per person while providing a clear implementation path for high-frequency vehicle testing content and automotive marketing asset production.",
                  "橙果视界围绕新车测试场景，设计 AI 图文与视频内容批量生成方案，将单车图文、询底价模板、视频分镜与 AI 述品结构整合为可配置、可复用、可规模化生产的内容工作流。方案通过车型素材上传、模板选择、文字替换、代码批量排版与 AI 生成能力联动，帮助客户将原本依赖人工逐条制作的汽车内容，升级为高产能、低成本、可快速验证的智能化生产系统，显著提升单人日均内容产出效率，并为高频新车测试内容、询底价素材与汽车营销内容的规模化生产提供清晰落地路径。",
                ),
                bullets: [
                  t("Daily Content Output per Person 8x", "单人日均内容产出提升 8x"),
                  t("Overall Production Cost Reduction 70%+", "综合内容制作成本降低 70%+"),
                ],
              },
              {
                brand: t("BLUETTI", "BLUETTI"),
                logo: bluettiLogo,
                thumb: bluettiThumb,
                tags: [t("Energy", "能源"), t("Global Marketing", "全球营销")],
                title: t("BLUETTI", "BLUETTI"),
                desc: t(
                  "Oran AI designed an AI-powered content production solution for the global marketing needs of portable power and home backup products, covering short videos, paid social ads, social seeding, and e-commerce conversion scenarios. Starting from user scenarios, core product selling points, consumer decision paths, and platform-specific content styles, the solution transformed outdoor camping, RV travel, emergency backup, and home energy management scenarios into continuously scalable marketing assets. Through script generation, selling point breakdown, asset repurposing, multilingual adaptation, and ad creative testing, Oran AI helped the brand improve overseas content supply efficiency, creative iteration speed, and conversion performance.",
                  "橙果视界围绕便携储能与家庭备电产品的海外营销需求，设计面向短视频、信息流广告、社媒种草与电商转化场景的 AI 内容生产方案。方案从用户使用场景、产品核心卖点、消费决策路径与平台内容风格出发，将户外露营、房车旅行、应急备电、家庭能源管理等场景转化为可持续产出的营销素材，并通过脚本生成、卖点拆解、素材复用、多语言适配与广告创意测试，帮助品牌提升海外市场内容供给效率、广告素材迭代速度与用户转化表现。",
                ),
                bullets: [
                  t("Global Marketing Content Matrix Upgrade", "出海营销内容矩阵升级"),
                  t("Product Selling Point Conversion Enhancement", "产品卖点转化效率提升"),
                ],
              },
              {
                brand: t("zdeer", "左点"),
                logo: zdeerLogo,
                thumb: zdeerThumb,
                tags: [t("Healthcare", "医疗"), t("Product Visuals", "产品视觉")],
                title: t("zdeer", "左点"),
                desc: t(
                  "Oran AI provided AI-powered product visual generation services for the client’s online marketing needs across smart health and personal care products. By combining product functions, usage scenarios, consumer profiles, and platform display requirements, the solution transformed product selling points into more scenario-driven, credible, and conversion-oriented visual assets. This helped the client accelerate product asset deployment, improve visual content iteration speed, and establish a more consistent and refined product expression across official websites, e-commerce platforms, and key marketing touchpoints.",
                  "橙果视界围绕客户智能健康与个人护理产品的线上营销需求，提供 AI 产品视觉生成服务，系统化产出可用于官网展示、购物平台详情页及营销传播的产品图片。项目结合产品功能、使用场景、消费人群与平台展示规范，将产品卖点转化为更具场景感、信任感与转化引导力的视觉内容，帮助客户提升商品素材上线效率与视觉内容更新速度，并在官网、电商平台及核心营销触点中形成更统一、更高质感的产品表达。",
                ),
                bullets: [
                  t("Product Asset Launch Efficiency 3x", "商品素材上线效率提升 3x"),
                  t("Visual Content Iteration Speed 60%+", "视觉内容更新迭代提速 60%+"),
                  t("Core Marketing Scenario Coverage Rate 50%+", "核心营销场景覆盖率提升 50%+"),
                ],
              },
              {
                brand: t("1MORE", "万魔"),
                logo: imoreLogo,
                thumb: imoreThumb,
                tags: [t("Audio", "音频"), t("Social Media", "社媒")],
                title: t("1MORE", "万魔"),
                desc: t(
                  "Oran AI provided AI-powered product visual generation services for the brand’s global social media marketing needs, producing visual assets for paid social campaigns, product hero images, and UGC-style content scenarios. Starting from the product’s design language, wearing experience, sound-quality positioning, and lifestyle use cases, the solution transformed headphone products into platform-adapted and consumer-relevant content assets. This helped the client accelerate global social creative iteration, enrich visual expression across channels, and strengthen product appeal and conversion performance throughout the consumer decision journey.",
                  "橙果视界围绕耳机产品的全球社媒营销需求，提供 AI 产品视觉生成服务，系统化产出可用于社媒投放、产品主图与 UGC 化内容场景的视觉素材。项目从产品设计语言、佩戴体验、音质卖点与生活方式场景出发，将耳机产品转化为更具平台适配度和用户代入感的内容资产，帮助客户提升全球社媒创意迭代效率，丰富品牌在不同渠道中的视觉表达，并强化产品在消费决策链路中的种草力与转化表现。",
                ),
                bullets: [
                  t("Global Social Creative Iteration Efficiency 70%+", "全球社媒创意迭代效率提升 70%+"),
                  t("UGC-Style Content Asset Share 60%+", "UGC 化内容资产占比提升 60%+"),
                ],
              },
              {
                brand: t("TRAJECTRY", "TRAJECTRY"),
                logo: trajectryLogo,
                thumb: trajectryThumb,
                tags: [t("Consulting", "咨询"), t("China Market", "中国市场")],
                title: t("TRAJECTRY", "TRAJECTRY"),
                desc: t(
                  "Oran AI designed a content optimization solution for the client’s marketing needs across China market insights, innovation consulting, and digital transformation services. Covering official websites, social media communication, and business presentation scenarios, the solution translated abstract capabilities such as strategic consulting, digital innovation, consumer insights, and growth services into clearer and more persuasive brand content assets. This helped the client strengthen market awareness, improve business communication efficiency, and enhance lead conversion for professional service offerings.",
                  "橙果视界围绕客户在中国市场洞察、创新咨询与数字化转型服务中的营销表达需求，设计面向官网、社媒传播与商务展示场景的内容优化方案。方案从客户服务能力、行业案例、目标客群与中国市场语境出发，将策略咨询、数字化创新、消费者洞察与增长服务等抽象能力，转化为更清晰、更具说服力的品牌内容资产，帮助客户提升专业服务的市场认知度、商务沟通效率与潜在客户转化能力。",
                ),
                bullets: [
                  t("China Market Content Narrative Upgrade", "中国市场内容表达升级"),
                  t("Consulting Service Conversion Enhancement", "咨询服务营销转化增强"),
                ],
              },
              {
                brand: t("Timekettle", "时空壶"),
                logo: timekettleLogo,
                thumb: timekettleThumb,
                tags: [t("Translation", "翻译"), t("Overseas", "海外")],
                title: t("Timekettle", "时空壶"),
                desc: t(
                  "Oran AI provided AI-powered visual and short-form video content production services for the client’s overseas social media and product marketing needs, helping the brand build a more efficient and consistent content asset system. Starting from product characteristics, brand identity, and overseas platform communication contexts, the solution transformed product value into refined, platform-adapted visual content, supporting the brand in improving asset iteration efficiency, visual consistency, and global communication performance across social media campaigns, content updates, and user engagement.",
                  "橙果视界围绕客户海外社媒传播与产品内容营销需求，提供 AI 视觉与短视频内容生产服务，帮助品牌构建更高效、更统一的内容资产体系。项目从产品特性、品牌调性与海外平台传播语境出发，将产品价值转化为更具质感和平台适配度的视觉内容，支持品牌在社媒投放、内容更新与用户触达中持续提升素材迭代效率、视觉表达一致性与全球市场传播表现。",
                ),
                bullets: [
                  t("Overseas Social Content Iteration Efficiency 60%+", "海外社媒内容迭代效率提升 60%+"),
                  t("Brand Visual Asset System Upgrade", "品牌视觉资产体系升级"),
                ],
              },
              {
                brand: t("XINGYUN GROUP", "行云科技"),
                logo: xingyunLogo,
                thumb: xingyunThumb,
                tags: [t("Independent Website", "独立站"), t("Visual Assets", "视觉资产")],
                title: t("XINGYUN GROUP", "行云科技"),
                desc: t(
                  "Oran AI provided systematic AI-powered product visual generation services for the client during the preparation of its independent website, helping transform basic product display into a more refined and commercially effective visual content system. Starting from page presentation, user browsing experience, and brand aesthetic expression, the solution translated product details, wearing scenarios, and creative concepts into consistent, polished, and conversion-oriented visual assets. This improved the client’s creative visual iteration efficiency during website development while strengthening overall page professionalism and brand image.",
                  "橙果视界围绕客户独立站筹备阶段的视觉内容需求，提供系统化 AI 产品视觉生成服务，帮助客户将基础商品展示升级为更具品牌质感与商业表现力的视觉内容体系。项目从独立站页面呈现、用户浏览体验与品牌审美表达出发，将产品细节、穿着氛围与创意概念转化为统一、精致且具备转化引导力的视觉资产，提升客户在独立站搭建过程中的创意方案迭代效率，并强化页面整体专业度与品牌形象。",
                ),
                bullets: [
                  t("Independent Website Visual Asset System Upgrade", "独立站视觉资产体系升级"),
                  t("Creative Visual Solution Iteration Efficiency 3x", "创意视觉方案迭代效率提升 3x"),
                ],
              },
              {
                brand: t("matrix", "matrix"),
                logo: matrixLogo,
                thumb: matrixThumb,
                tags: [t("Interior Design", "室内设计"), t("Digital Assets", "数字资产")],
                title: t("matrix", "matrix"),
                desc: t(
                  "Oran AI built an enterprise-grade structured digital asset management platform for the client, creating a unified asset foundation for content collection, storage, management, retrieval, collaboration, and distribution in the AIGC era. Through multi-dimensional tagging, scenario-based asset spaces, multi-user online collaboration, AI-powered search, granular permission control, and secure internal and external sharing, the platform transformed scattered images, videos, and design materials across different teams, channels, and projects into a manageable, reusable, and traceable asset system. This helped the client significantly improve asset retrieval and team collaboration efficiency while reducing duplicated production costs and data security risks, providing a stable digital foundation for long-term brand content operations.",
                  "橙果视界为客户搭建企业级结构化数字资产管理平台，围绕 AIGC 时代品牌内容资产的采集、入库、管理、检索、协同与分发需求，构建统一的数字资产底座。平台通过多维标签化管理、多场景空间管理、多端多人在线协同、AI 智能检索、精细化权限配置与团队内外安全分享等能力，将分散在不同业务、渠道与项目中的图片、视频及设计素材统一沉淀为可管理、可复用、可追踪的资产体系，帮助客户显著提升素材检索效率与团队协作效率，降低重复制作成本与数据泄露风险，为长期品牌内容运营提供稳定的数字化基础。",
                ),
                bullets: [
                  t("Annual Cost Savings Over RMB 3 million", "年度成本节省 累计 300万+"),
                  t("Asset Retrieval Efficiency 85% improvement", "资产检索效率提升 85%"),
                  t("Data Security Risk Reduction 50%", "数据安全风险降低 50%"),
                ],
              },
            ]}
          />
        </div>
      </section>

      

      {/* FINAL CTA */}
      <section className="relative py-32 px-6 overflow-hidden">
        <div className="absolute inset-0 opacity-50">
          <Nebula groups={4} perGroup={18} />
        </div>
        <div className="relative z-10 mx-auto max-w-4xl text-center">
          <h2 className="text-5xl md:text-7xl font-semibold tracking-tight leading-[1.05]">
            {t(
              <>
                Stop Betting.
                <br />
                <span className="brand-text">Start Knowing.</span>
              </>,
              <>
                告别未知，
                <br />
                <span className="brand-text">开始预见。</span>
              </>,
            )}
          </h2>
          <p className="mt-10 max-w-xl mx-auto text-muted-foreground">
            {t(
              "Enter your brand name. Get your first diagnosis in 5 minutes.",
              "输入品牌名，5分钟就能拿到你的第一份诊断报告。",
            )}
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <button className="btn-secondary" onClick={() => setFormOpen(true)}>
              {t("Contact us", "联系我们")}
            </button>
            <a
              className="btn-primary"
              href={config.toolbox.baseUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("Explore Products", "探索产品")} <ArrowRight size={16} />
            </a>
          </div>
          <p className="mt-10 text-xs text-muted-foreground">
            {t(
              "No credit card required · Setup in minutes · Built to fuel every step of your growth.",
              "无需信用卡 · 几分钟即用 · 为你的每一步增长注入动力",
            )}
          </p>
        </div>
      </section>

      <LeadFormDialog open={formOpen} onOpenChange={setFormOpen} />
    </div>
  );
};

export default HomePage;
